import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PopoverService } from '@services';
import { FolderLocationInfo, MultiLocationScheduleConfig, WorkScheduleType } from '@services/work-schedule/model';
import { WorkScheduleService } from '@services/work-schedule/work-schedule.service';

export interface ScheduleEditModalConfig {
  subjectType: 'folder' | 'asset' | 'team';
  subjectID: number;
  subjectName?: string;

  // Current schedule selections
  workDaysScheduleID: number;
  daysOffScheduleID: number;

  // Inherited values for display
  inheritedWorkDaysScheduleID?: number;
  inheritedWorkDaysName?: string;
  inheritedDaysOffScheduleID?: number;
  inheritedDaysOffName?: string;

  // Effective schedule IDs (resolved through inheritance chain)
  effectiveWorkDaysScheduleID?: number;
  effectiveDaysOffScheduleID?: number;

  // Label for inheritance source
  inheritLabel?: string; // e.g., "(Folder Default)" or "(Location Default)"

  // Multi-location support
  multiLocationMode?: boolean;                     // True if folder spans multiple locations
  locations?: FolderLocationInfo[];                // Locations with asset counts
  multiLocationConfig?: MultiLocationScheduleConfig;  // Current per-location schedule config
}

export interface ScheduleEditModalResult {
  workDaysScheduleID: number;
  daysOffScheduleID: number;
  changed: boolean;
  multiLocationConfig?: MultiLocationScheduleConfig;  // Per-location config if using multi-location mode
}

@Component({
  selector: 'app-schedule-edit-modal',
  templateUrl: './schedule-edit-modal.component.html',
  styleUrls: ['./schedule-edit-modal.component.scss']
})
export class ScheduleEditModalComponent implements OnInit {
  @Input() config: ScheduleEditModalConfig;
  public WorkScheduleType = WorkScheduleType;

  // Form values
  workDaysScheduleID: number;
  daysOffScheduleID: number;

  // Original values for change detection
  private originalWorkDaysScheduleID: number;
  private originalDaysOffScheduleID: number;

  // Multi-location config
  multiLocationConfig: MultiLocationScheduleConfig;
  private originalMultiLocationConfig: MultiLocationScheduleConfig;
  private multiLocationConfigChanged: boolean = false;

  // Schedule options
  workDaysScheduleOptions: any[] = [];
  daysOffScheduleOptions: any[] = [];

  // Resolved effective schedule IDs (for entries editors and calendar)
  effectiveWorkDaysScheduleID: number = 0;
  effectiveDaysOffScheduleID: number = 0;

  constructor(
    private modalController: ModalController,
    private translate: TranslateService,
    private workScheduleService: WorkScheduleService,
    private popoverService: PopoverService
  ) {}

  ngOnInit() {

    // Store original values
    this.originalWorkDaysScheduleID = this.config.workDaysScheduleID;
    this.originalDaysOffScheduleID = this.config.daysOffScheduleID;


    // Set current values
    this.workDaysScheduleID = this.config.workDaysScheduleID;
    this.daysOffScheduleID = this.config.daysOffScheduleID;

    // Initialize multi-location config if in multi-location mode
    if (this.config.multiLocationMode && this.config.multiLocationConfig) {
      this.multiLocationConfig = JSON.parse(JSON.stringify(this.config.multiLocationConfig));
      this.originalMultiLocationConfig = JSON.parse(JSON.stringify(this.config.multiLocationConfig));
    } else if (this.config.multiLocationMode && this.config.locations?.length > 1) {
      // Initialize default multi-location config
      this.multiLocationConfig = {
        mode: 'per-location',
        global: {
          workDaysScheduleID: this.config.workDaysScheduleID,
          daysOffScheduleID: this.config.daysOffScheduleID
        },
        locations: {}
      };
      this.originalMultiLocationConfig = null;
    }

    // Build schedule options with inherit option
    this.buildScheduleOptions();

    // Calculate effective schedule IDs
    this.updateEffectiveScheduleIDs();
  }

  private buildScheduleOptions() {
    const workDaysSchedules = this.workScheduleService.getWorkSchedulesByType(WorkScheduleType.Work_Days);
    const daysOffSchedules = this.workScheduleService.getWorkSchedulesByType(WorkScheduleType.Days_Off);

    const inheritLabel = this.config.inheritLabel || this.translate.instant('WORK_SCHEDULE.Inherited');

    // Build work days options with inherit option first
    const inheritedWorkDaysName = this.config.inheritedWorkDaysName ||
      this.translate.instant('WORK_SCHEDULE.Location_Default');
    this.workDaysScheduleOptions = [
      { scheduleID: -1, name: `${inheritLabel}: ${inheritedWorkDaysName}` },
      ...workDaysSchedules
    ];

    // Build days off options with inherit option first
    const inheritedDaysOffName = this.config.inheritedDaysOffName ||
      this.translate.instant('WORK_SCHEDULE.Location_Default');
    this.daysOffScheduleOptions = [
      { scheduleID: -1, name: `${inheritLabel}: ${inheritedDaysOffName}` },
      ...daysOffSchedules
    ];
  }

  /**
   * Update effective schedule IDs when selection changes
   * Used for the entries editors and calendar preview
   */
  updateEffectiveScheduleIDs() {
    // Work days: use selected if > 0, otherwise inherited, otherwise find default
    if (this.workDaysScheduleID > 0) {
      this.effectiveWorkDaysScheduleID = this.workDaysScheduleID;
    } else if (this.config.inheritedWorkDaysScheduleID && this.config.inheritedWorkDaysScheduleID > 0) {
      this.effectiveWorkDaysScheduleID = this.config.inheritedWorkDaysScheduleID;
    } else {
      this.effectiveWorkDaysScheduleID = this.getDefaultScheduleID(WorkScheduleType.Work_Days);
    }

    // Days off: use selected if > 0, otherwise inherited, otherwise find default
    if (this.daysOffScheduleID > 0) {
      this.effectiveDaysOffScheduleID = this.daysOffScheduleID;
    } else if (this.config.inheritedDaysOffScheduleID && this.config.inheritedDaysOffScheduleID > 0) {
      this.effectiveDaysOffScheduleID = this.config.inheritedDaysOffScheduleID;
    } else {
      this.effectiveDaysOffScheduleID = this.getDefaultScheduleID(WorkScheduleType.Days_Off);
    }
  }

  private getDefaultScheduleID(type: WorkScheduleType): number {
    const schedules = this.workScheduleService.getWorkSchedulesByType(type);
    const defaultSchedule = schedules.find((s: any) => s.isDefault);
    if (defaultSchedule) {
      return defaultSchedule.scheduleID;
    }
    if (schedules.length > 0) {
      return schedules[0].scheduleID;
    }
    return 0;
  }

  onWorkDaysChange() {
    this.updateEffectiveScheduleIDs();
  }

  onDaysOffChange() {
    this.updateEffectiveScheduleIDs();
  }

  /**
   * Handle changes from the multi-location schedule editor
   */
  onMultiLocationConfigChange(config: MultiLocationScheduleConfig) {
    this.multiLocationConfig = config;
    this.multiLocationConfigChanged = true;
  }

  get hasChanges(): boolean {
    // Check multi-location changes if in multi-location mode
    if (this.config.multiLocationMode && this.config.locations?.length > 1) {
      return this.multiLocationConfigChanged;
    }
    // Single-location mode
    return this.workDaysScheduleID !== this.originalWorkDaysScheduleID ||
           this.daysOffScheduleID !== this.originalDaysOffScheduleID;
  }

  get subjectTypeLabel(): string {
    return this.config.subjectType === 'folder'
      ? this.translate.instant('SHARED.Folder')
      : this.translate.instant('SHARED.Asset');
  }

  async save() {
    this.popoverService.show({
      title: this.translate.instant('WORK_SCHEDULE.Update_Schedule'),
      description: this.translate.instant('WORK_SCHEDULE.Update_Schedule_Confirm'),
      onSave: async () => {
        const result: ScheduleEditModalResult = {
          workDaysScheduleID: this.workDaysScheduleID,
          daysOffScheduleID: this.daysOffScheduleID,
          changed: this.hasChanges
        };

        // Include multi-location config if in multi-location mode
        if (this.config.multiLocationMode && this.config.locations?.length > 1 && this.multiLocationConfig) {
          result.multiLocationConfig = this.multiLocationConfig;
        }

        await this.modalController.dismiss(result, 'save');
      }
    });
  }

  async cancel() {
    if (this.hasChanges) {
      this.popoverService.show({
        title: this.translate.instant('WORK_SCHEDULE.Discard_Changes'),
        description: this.translate.instant('WORK_SCHEDULE.Discard_Changes_Confirm'),
        onSave: async () => {
          await this.modalController.dismiss(null, 'cancel');
        }
      });
    } else {
      await this.modalController.dismiss(null, 'cancel');
    }
  }
}
